/*
 * Terms acceptance gating (SPID-232).
 *
 * Gates every checkout path the theme can actually reach: the cart page, the
 * cart drawer and the cart notification. Theme code cannot gate a direct
 * /checkout URL or a cart permalink - see docs/terms-acceptance-open-routes.md
 * for the routes that remain open and why.
 *
 * Design notes:
 *
 * - All listeners are delegated on `document`. The cart drawer and the cart
 *   notification both re-render themselves with innerHTML, which would silently
 *   drop any listener bound directly to a button.
 *
 * - Checkout buttons are never `disabled`. A disabled button is unreachable by
 *   keyboard, is skipped by screen readers and explains nothing. They stay
 *   focusable with aria-disabled="true"; the click is intercepted and the
 *   reason is announced through a role="alert" message instead.
 *
 * - The checkbox also carries the native `required` attribute, so browser form
 *   validation gates the three form submits even when this script has not run.
 *
 * - Acceptance is deliberately NOT persisted across surfaces. Counsel asked for
 *   an unchecked clickwrap, so each surface asks for its own affirmative tick
 *   rather than restoring a previous one.
 */
(function () {
  'use strict';

  var GATE = '[data-terms-gate]';
  var GUARD = '[data-terms-guard]';
  var EXPRESS = '[data-terms-guard-express]';

  function gateFor(context) {
    if (!context) return null;
    return document.querySelector(GATE + '[data-terms-context="' + context + '"]');
  }

  function isAccepted(gate) {
    var box = gate && gate.querySelector('[data-terms-checkbox]');
    return !!(box && box.checked);
  }

  function showError(gate) {
    var error = gate.querySelector('[data-terms-error]');
    if (error) error.hidden = false;
  }

  function hideError(gate) {
    var error = gate.querySelector('[data-terms-error]');
    if (error) error.hidden = true;
  }

  function focusCheckbox(gate) {
    var box = gate.querySelector('[data-terms-checkbox]');
    if (box) box.focus({ preventScroll: false });
  }

  /* Fill the hidden inputs that ride along with the cart form POST. */
  function writeEvidenceFields(gate, accepted) {
    var values = {
      accepted: accepted ? 'Yes' : '',
      at: accepted ? new Date().toISOString() : '',
      version: accepted ? gate.getAttribute('data-terms-version') || '' : '',
      via: accepted ? gate.getAttribute('data-terms-context-label') || '' : '',
    };

    Object.keys(values).forEach(function (key) {
      var field = gate.querySelector('[data-terms-attr="' + key + '"]');
      if (field) field.value = values[key];
    });

    return values;
  }

  /*
   * Also write the evidence straight onto the cart. The hidden fields only
   * travel with our own form POST, which the accelerated wallet buttons bypass -
   * this makes the record stick regardless of which control finishes the order.
   */
  function writeCartAttributes(values) {
    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Terms Accepted': values.accepted,
          'Terms Accepted At': values.at,
          'Terms Version': values.version,
          'Terms Accepted Via': values.via,
        },
      }),
    }).catch(function () {
      /* The hidden form fields still carry the record, so a failure here is
         not worth interrupting checkout for. */
    });
  }

  /* Reflect the gate state onto every control it guards. */
  function syncGate(gate) {
    var context = gate.getAttribute('data-terms-context');
    var accepted = isAccepted(gate);

    document.querySelectorAll(GUARD + '[data-terms-guard="' + context + '"]').forEach(function (control) {
      control.setAttribute('data-terms-blocked', accepted ? 'false' : 'true');
      if (accepted) {
        control.removeAttribute('aria-disabled');
      } else {
        control.setAttribute('aria-disabled', 'true');
      }
    });

    document.querySelectorAll(EXPRESS + '[data-terms-guard-express="' + context + '"]').forEach(function (wrapper) {
      wrapper.setAttribute('data-terms-blocked', accepted ? 'false' : 'true');
      /* `inert` keeps the wallet iframes out of the tab order and away from
         assistive tech while they are blocked. */
      if (accepted) {
        wrapper.removeAttribute('inert');
      } else {
        wrapper.setAttribute('inert', '');
      }
      ensureVeil(wrapper, context);
    });
  }

  /*
   * Wallet buttons live inside Shopify-rendered iframes, so their clicks cannot
   * be intercepted from here. A transparent button sits on top while the gate is
   * closed: it takes the click and explains what is missing.
   */
  function ensureVeil(wrapper, context) {
    if (wrapper.querySelector('.terms-gate-express__veil')) return;

    var veil = document.createElement('button');
    veil.type = 'button';
    veil.className = 'terms-gate-express__veil';
    veil.setAttribute('data-terms-veil', context);
    veil.setAttribute('aria-label', 'Accept the Terms of Service to use express checkout');
    wrapper.appendChild(veil);
  }

  function syncAll() {
    document.querySelectorAll(GATE).forEach(syncGate);
  }

  /* Block, explain, and put the customer on the control that unblocks it. */
  function refuse(gate, event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    showError(gate);
    focusCheckbox(gate);
  }

  document.addEventListener(
    'change',
    function (event) {
      var box = event.target.closest && event.target.closest('[data-terms-checkbox]');
      if (!box) return;

      var gate = box.closest(GATE);
      if (!gate) return;

      var values = writeEvidenceFields(gate, box.checked);
      if (box.checked) {
        hideError(gate);
        writeCartAttributes(values);
      }
      syncGate(gate);
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target.closest) return;

      var veil = target.closest('[data-terms-veil]');
      if (veil) {
        var veilGate = gateFor(veil.getAttribute('data-terms-veil'));
        if (veilGate) refuse(veilGate, event);
        return;
      }

      var control = target.closest(GUARD);
      if (!control) return;

      var gate = gateFor(control.getAttribute('data-terms-guard'));
      if (gate && !isAccepted(gate)) refuse(gate, event);
    },
    true
  );

  /* Catches Enter-key submits, which never produce a click on the button. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.id) return;

      var gate = document.querySelector(GATE + '[data-terms-form="' + form.id + '"]');
      if (gate && !isAccepted(gate)) refuse(gate, event);
    },
    true
  );

  /*
   * The drawer and the notification replace their own markup on every cart
   * update, which resets the checkbox and drops the blocked state. Re-syncing on
   * mutation is what keeps the gate closed across those re-renders.
   */
  function observe() {
    var pending = false;

    /* setTimeout rather than requestAnimationFrame: rAF is suspended in a
       background tab, which would leave the blocked styling and aria-disabled
       stale until the tab was looked at again. The gate itself never depends on
       this - every interception reads the checkbox live - but the state the
       customer sees should not lag either. */
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        syncAll();
      }, 0);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    syncAll();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
